import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Box, Select, TextField } from '@hiiretail/synergy-ui';
import {
  Controller,
  FieldValues,
  UseFieldArrayAppend,
  UseFieldArrayReturn,
  UseFieldArrayUpdate,
  UseFormReturn,
  UseFormSetValue,
} from 'react-hook-form';
import { CommonMessages } from '../../../../../../common';
import { IdentityProvidersMessages } from '../../../../IdentityProviders.messages';
import { Option } from '@hiiretail/synergy-ui/dist/types/components/inputs/Select/Select.types';
import { promptOptions } from '../../../../utils/propmtOptions';
import { useIntl } from 'react-intl';
import { CertificatesList } from '../../../CertificatesList/CertificatesList';
import { ExternalIdpLogoutConfigForm } from 'pages/IdentityProviders/ui/IdentityProvidersList/components/ExternalIdpLogoutConfigForm/ExternalIdpLogoutConfigForm';

type CreateSamlProviderFormProps = {
  control: UseFormReturn['control'];
  formState: UseFormReturn['formState'];
  resetField: UseFormReturn['resetField'];
  trigger: UseFormReturn['trigger'];
  fields: UseFieldArrayReturn['fields'];
  append: UseFieldArrayAppend<FieldValues, 'certificates'>;
  remove: UseFieldArrayReturn['remove'];
  update: UseFieldArrayUpdate<FieldValues, 'certificates'>;
  setValue: UseFormSetValue<FieldValues>;
};

type CreateSamlProviderFormComponent = React.FC<CreateSamlProviderFormProps>;

export const CreateSamlProviderForm: CreateSamlProviderFormComponent = ({
  control,
  formState,
  resetField,
  fields,
  append,
  remove,
  update,
  trigger,
  setValue,
}) => {
  const intl = useIntl();

  const [showIdpLogoutConfig, setShowIdpLogoutConfig] = useState<boolean>(false);
  const handleAddIdpLogoutConfig = useCallback(() => {
    setShowIdpLogoutConfig((value) => !value);

    if (showIdpLogoutConfig) {
      setValue('externalIdpLogoutConfig', undefined);
    } else {
      setValue('externalIdpLogoutConfig.enabled', true);
      setValue('externalIdpLogoutConfig.redirectUrl', '');
    }
    trigger('externalIdpLogoutConfig', { shouldFocus: true });
  }, [setValue, showIdpLogoutConfig, trigger]);

  const onFileLoad = (index: number) => async (event: ChangeEvent<HTMLInputElement>) => {
    // @ts-ignore
    const file = event.target.files[0];
    const text = await file.text();
    const formattedText = text.replace(/\r/gm, '').trim();
    update(index, { value: formattedText });
    trigger(`certificates[${index}].value`);
  };

  useEffect(() => {
    resetField('certificates', {
      defaultValue: [{ value: '' }],
    });
    resetField('rpEntityId', {
      defaultValue: window.location.origin,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form>
      <Box mb={3}>
        <Controller
          name="name"
          defaultValue=""
          control={control}
          render={({ field: { ref, value, onChange } }) => (
            <TextField
              tabIndex={0}
              fullWidth
              required
              label={intl.formatMessage(CommonMessages.name)}
              name="name"
              type="text"
              ref={ref}
              // @ts-ignore
              hint={
                <span>
                  Unique name for the identity provider
                  <br />
                  Must be between 3 and 256 characters long
                </span>
              }
              value={value}
              onChange={onChange}
              error={formState.errors?.name?.message as string | undefined}
            />
          )}
        />
      </Box>
      <Box mb={3}>
        <Controller
          name="externalIdField"
          defaultValue=""
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              fullWidth
              required
              label={intl.formatMessage(CommonMessages.externalIdField)}
              name="externalIdField"
              value={value?.value}
              isClearable={false}
              isSearchable={false}
              // @ts-ignore
              hint={
                <span>
                  Specifies which field will be used by Hii Retail as a{' '}
                  <strong>unique user identifier</strong>,<br /> when importing users from the 3rd
                  party identity platform
                </span>
              }
              placeholder={intl.formatMessage(IdentityProvidersMessages.selectExternalIdField)}
              onChange={(option: Option | null) => onChange(option?.value)}
              error={formState.errors?.externalIdField?.message as string | undefined}
              options={[
                { label: intl.formatMessage(CommonMessages.uid), value: 'Uid' },
                { label: intl.formatMessage(CommonMessages.email), value: 'Email' },
                { label: intl.formatMessage(CommonMessages.phoneNumber), value: 'PhoneNumber' },
              ]}
            />
          )}
        />
      </Box>
      <Box mb={3}>
        <Controller
          name="userType"
          defaultValue=""
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              fullWidth
              required
              label={intl.formatMessage(CommonMessages.userType)}
              name="userType"
              // @ts-ignore
              hint={
                <span>
                  Specifies which type of the user the identity provider will handle:
                  <br />
                  <strong>Staff</strong> users represent <strong>retail employees</strong>
                  <br />
                  <strong>Shoppers</strong> users represent <strong>retail customers</strong>
                  <br />
                </span>
              }
              value={value?.value}
              isClearable={false}
              isSearchable={false}
              placeholder={intl.formatMessage(IdentityProvidersMessages.selectUserType)}
              onChange={(option: Option | null) => onChange(option?.value)}
              error={formState.errors?.userType?.message as string | undefined}
              options={[
                { label: intl.formatMessage(CommonMessages.staff), value: 'Staff' },
                { label: intl.formatMessage(CommonMessages.shoppers), value: 'Shoppers' },
              ]}
            />
          )}
        />
      </Box>
      <Box mb={3}>
        <Controller
          name="entityId"
          defaultValue=""
          control={control}
          render={({ field: { ref, value, onChange } }) => (
            <TextField
              tabIndex={0}
              fullWidth
              required
              label="Entity ID"
              name="entityId"
              hint="An identifier of an app in Identity provider, usually is in form of URL. Can not be longer than 512 characters"
              type="text"
              ref={ref}
              value={value}
              onChange={onChange}
              error={formState.errors?.entityId?.message as string | undefined}
            />
          )}
        />
      </Box>
      <Box mb={3}>
        <Controller
          name="ssoUrl"
          defaultValue=""
          control={control}
          render={({ field: { ref, value, onChange } }) => (
            <TextField
              tabIndex={0}
              fullWidth
              required
              // @ts-ignore
              hint={
                <span>
                  Provider's single sign-on URL
                  <br />
                </span>
              }
              label="SSO URL"
              name="ssoUrl"
              type="text"
              ref={ref}
              value={value}
              onChange={onChange}
              error={formState.errors?.ssoUrl?.message as string | undefined}
            />
          )}
        />
      </Box>
      <Box mb={3}>
        <Controller
          name="rpEntityId"
          defaultValue=""
          control={control}
          render={({ field: { ref, value, onChange } }) => (
            <TextField
              tabIndex={0}
              fullWidth
              required
              label="Relaying party entity ID"
              name="rpEntityId"
              type="text"
              // @ts-ignore
              hint={
                <span>
                  The app URL, for most of the use cases you can use default value. On your SAML
                  identity provider, this is referred to as the audience
                </span>
              }
              ref={ref}
              value={value}
              onChange={onChange}
              error={formState.errors?.rpEntityId?.message as string | undefined}
            />
          )}
        />
      </Box>
      <Box mb={3}>
        <Controller
          name="prompt"
          control={control}
          defaultValue={'select_account'}
          render={({ field: { value, onChange } }) => (
            <Select
              label={intl.formatMessage(CommonMessages.prompt)}
              name="prompt"
              value={value?.value}
              error={formState.errors?.prompt?.message as string | undefined}
              isSearchable={false}
              placeholder={value}
              hint="Specifies whether the Authorization Server prompts the End-User for reauthentication and consent"
              isClearable={false}
              initialValue={{ label: 'Select account', value: 'select_account' }}
              onChange={(option: Option | null) => onChange(option?.value)}
              options={promptOptions}
              fullWidth
            />
          )}
        />
      </Box>
      <ExternalIdpLogoutConfigForm
        control={control}
        formState={formState}
        isOpen={showIdpLogoutConfig}
        handleAddForm={handleAddIdpLogoutConfig}
      />
      <CertificatesList
        fields={fields}
        control={control}
        formState={formState}
        onFileLoad={onFileLoad}
        remove={remove}
        append={append}
      />
      <Controller render={() => <span />} control={control} name="type" defaultValue="Saml" />
    </form>
  );
};
